"use strict";
/**
 * Shared, zero-network VIN validation — used by both apps/web (before ever
 * calling the API) and apps/api (before ever calling NHTSA). Catches the
 * overwhelming majority of typos for free.
 *
 * NHTSA vPIC's `ErrorCode` is a comma-separated list and is NOT a simple
 * allowlist — verified live against three cases:
 *
 *   "1HGCM82633A004352" (17 chars)  -> ErrorCode "0"      -> clean
 *   "JTHBJ46G"           (8 chars)  -> ErrorCode "6,12"    -> usable (partial VIN + explicit modelyear)
 *   "1HGCM82633A00435X" (17 chars)  -> ErrorCode "1,400"   -> STILL returns plausible Make/Model
 *
 * The third case is the important one: NHTSA returns plausible fields even
 * for a VIN containing invalid characters, so "fields came back" never means
 * "the VIN is valid". Use `isDecodeUsableForDisplay` for prefill (always
 * true — surface a warning, never block) and `isVinCanonical` to decide
 * whether a VIN may be treated as the definitive identifier for a vehicle.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.normalizeVin = normalizeVin;
exports.vinLengthOk = vinLengthOk;
exports.vinHasIllegalChars = vinHasIllegalChars;
exports.vinCheckDigitValid = vinCheckDigitValid;
exports.vinModelYear = vinModelYear;
exports.isDecodeUsableForDisplay = isDecodeUsableForDisplay;
exports.isVinCanonical = isVinCanonical;
const ILLEGAL_VIN_CHARS = new Set(["I", "O", "Q"]);
/** Uppercase, strip spaces/hyphens. Does not validate. */
function normalizeVin(raw) {
    return raw.toUpperCase().replace(/[\s-]/g, "");
}
function vinLengthOk(vin) {
    return vin.length === 17;
}
/** Returns the first illegal character found (I, O, or Q — excluded from the VIN alphabet), or null. */
function vinHasIllegalChars(vin) {
    for (const ch of vin) {
        if (ILLEGAL_VIN_CHARS.has(ch))
            return ch;
    }
    return null;
}
const VIN_TRANSLITERATION = {
    A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8,
    J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9,
    S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9,
};
const VIN_WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];
/** ISO-3779 / NHTSA check digit at position 9 (index 8). Requires exactly 17 characters. */
function vinCheckDigitValid(vin) {
    if (vin.length !== 17)
        return false;
    let sum = 0;
    for (let i = 0; i < 17; i++) {
        const ch = vin.charAt(i);
        const value = ch >= "0" && ch <= "9" ? Number(ch) : VIN_TRANSLITERATION[ch];
        if (value === undefined)
            return false;
        sum += value * (VIN_WEIGHTS[i] ?? 0);
    }
    const remainder = sum % 11;
    const expected = remainder === 10 ? "X" : String(remainder);
    return vin.charAt(8) === expected;
}
/**
 * Position 10 (index 9) model-year code. The sequence is inherently
 * ambiguous — it repeats every 30 years — so this resolves each half
 * using whichever cycle is plausible for a shop seeing the vehicle today
 * rather than always preferring the newer one:
 *
 *   letters A-Y (1980-2000 or 2010-2030) -> the 2010-2030 reading
 *   digits  1-9 (2001-2009 or 2031-2039) -> the 2001-2009 reading, since
 *                                           2031 has not occurred yet
 *
 * Only meaningful for a full, well-formed 17-char VIN. Treat the result as
 * a hint, not a fact — prefer whatever the work order's own model-year
 * field says, and never use this to reject a VIN.
 */
const YEAR_CODE_LETTERS = "ABCDEFGHJKLMNPRSTVWXY"; // excludes I, O, Q, U, Z, 0 per the VIN standard
const YEAR_CODE_DIGITS = "123456789";
function vinModelYear(vin) {
    if (vin.length !== 17)
        return null;
    const code = vin.charAt(9);
    const letterIndex = YEAR_CODE_LETTERS.indexOf(code);
    if (letterIndex !== -1)
        return 2010 + letterIndex;
    const digitIndex = YEAR_CODE_DIGITS.indexOf(code);
    if (digitIndex !== -1)
        return 2001 + digitIndex;
    return null;
}
/**
 * Whether a decode's fields are safe to prefill a form with. Always true if
 * ANY identifying field came back — NHTSA is an autofill convenience, never
 * a validation gate. The caller is responsible for surfacing ErrorCode as an
 * advisory warning banner, never as a blocking error.
 */
function isDecodeUsableForDisplay(fields) {
    return Boolean(fields.make || fields.model);
}
/**
 * Whether a VIN may be treated as the canonical identifier for a vehicle
 * (e.g. eligible for permanent caching keyed by VIN, or for the partial
 * unique index in the database). Requires 17 characters, a valid check
 * digit, and the absence of NHTSA error codes 1 (check digit failed) and
 * 400 (invalid characters present) — NOT merely "some fields came back".
 */
function isVinCanonical(vin, errorCode) {
    if (!vinLengthOk(vin))
        return false;
    if (vinHasIllegalChars(vin))
        return false;
    if (!vinCheckDigitValid(vin))
        return false;
    const codes = new Set((errorCode ?? "0").split(",").map((c) => c.trim()));
    if (codes.has("1") || codes.has("400"))
        return false;
    return true;
}
//# sourceMappingURL=vin.js.map